/**
 * CORE-AC — the server-side finish derivations (Core-100 R4).
 *
 * Every finish a browser used to compute and write itself (AutoBump, OptiFinish
 * pushback, the import sequence normaliser, Add Job's custom path, a timer start)
 * is now DERIVED HERE, on the server, from what the database holds — the browser
 * only states its intent (`ticket-finish-intent`). The formulas are the ones the
 * hooks applied, moved so the board keeps its behaviour; where two hooks derived
 * the SAME state two different ways, the one derivation below replaces both (named
 * at each function).
 *
 * PURE: no clock, no database, no timezone library. Callers pass `nowMs`, the
 * shop-local minute of day of each instant, and the break windows. That keeps the
 * derivation reproducible (CORE-AC: "MUST NOT read the clock or the database") and
 * importable by the browser (the break-buffer helpers are re-exported by
 * src/utils/breakBufferHelpers.ts — one copy, not two).
 *
 * DST (CORE-AC-GAP-004): a finish is the START INSTANT plus the elapsed minutes of
 * the work window (`finishFromStart`), never "the wall-clock minute of the finish
 * converted back on the start's date". Breaks stay wall-clock facts (lunch at 12:00
 * is 12:00 on a DST Sunday); only the conversion back to an instant changes, so a
 * job that crosses 02:00 local now spans exactly its planned minutes.
 */
import { calculateWallMinutes, calculateTicketOptiFinish, parseTimeToMinutes, planningBuffer } from './canonicalTimeMath.ts';

export const MINUTE_MS = 60_000;

export type BreakMinutes = { start: number; end: number };
export type DurationBreak = { start: number; end: number; durationMinutes: number; name?: string };
export type ShopBreak = { start: string; end: string; name?: string; date?: string };

// ─── planning inputs ────────────────────────────────────────────────────────

/**
 * The buffer a finish is planned with. A recorded number is used as recorded —
 * including 0 (the hooks' `|| 25` turned a recorded 0 into 25, so AutoBump and the
 * cascade disagreed for every zero-buffer technician). Unrecorded is planned as 0,
 * the value the cascade's own arithmetic has always given a NULL buffer. B-W4 (founder decision
 * 2026-10-18) settles that as policy: unconfigured = 0 %, explicit 0 = 0 %, no hidden 25 %. A recorded
 * value is clamped to the column's CHECK range 0..100 (schedulingDay.resolvePlanningBufferPct is this).
 */
export { planningBuffer };

/** A booked duration the derivation may use; anything else is UNKNOWN (CORE-AC: never invented). */
export function knownBookedMinutes(predicted: number | null | undefined): number | null {
  return typeof predicted === 'number' && Number.isFinite(predicted) && predicted > 0 ? predicted : null;
}

/** Wall minutes of work (OptiTime) for a known booked duration. */
export function optiTimeMinutes(bookedMinutes: number, efficiencyPct: number, bufferPct: number): number {
  return calculateWallMinutes(bookedMinutes, efficiencyPct, bufferPct);
}

// ─── breaks ─────────────────────────────────────────────────────────────────

/** A technician's breaks that apply on `dateStr` (recurring, then that day's one-off breaks), as written. */
export function techBreaksOn(recurring: unknown, nonRecurring: unknown, dateStr: string): ShopBreak[] {
  const rec = Array.isArray(recurring) ? (recurring as ShopBreak[]) : [];
  const one = Array.isArray(nonRecurring) ? (nonRecurring as ShopBreak[]).filter((b) => b?.date === dateStr) : [];
  return [...rec, ...one];
}

/** Break windows in shop-local minutes of day. */
export function breakMinutes(breaks: ShopBreak[]): BreakMinutes[] {
  return breaks.map((b) => ({ start: parseTimeToMinutes(b.start), end: parseTimeToMinutes(b.end) }));
}

/** Parse break windows for a specific date (moved from src/utils/breakBufferHelpers.ts). */
export function parseBreaksForDate(
  recurringBreaks: Array<{ start: string; end: string; name?: string }> | null,
  nonRecurringBreaks: Array<{ date: string; start: string; end: string; name?: string }> | null,
  dateStr: string,
): DurationBreak[] {
  const breaks: DurationBreak[] = [];
  const push = (brk: { start: string; end: string; name?: string }) => {
    const startMin = parseTimeToMinutes(brk.start);
    const endMin = parseTimeToMinutes(brk.end);
    if (!isNaN(startMin) && !isNaN(endMin)) {
      breaks.push({ start: startMin, end: endMin, durationMinutes: endMin - startMin, name: brk.name });
    }
  };
  for (const brk of recurringBreaks ?? []) push(brk);
  for (const brk of nonRecurringBreaks ?? []) if (brk.date === dateStr) push(brk);
  return breaks.sort((a, b) => a.start - b.start);
}

/** Does the finish fall inside a break window? (moved from breakBufferHelpers) */
export function findBreakOverlap(optiFinishMin: number, breaks: DurationBreak[]): DurationBreak | null {
  for (const brk of breaks) {
    if (optiFinishMin > brk.start && optiFinishMin <= brk.end) return brk;
  }
  return null;
}

/** Does the ticket's span (start to finish) overlap a break? (moved from breakBufferHelpers) */
export function ticketSpansBreak(startMin: number, optiFinishMin: number, breaks: DurationBreak[]): DurationBreak | null {
  for (const brk of breaks) {
    if (startMin < brk.end && optiFinishMin > brk.start) return brk;
  }
  return null;
}

export interface BreakBufferResult {
  case: 'A' | 'B' | 'none';
  breakBufferRemaining: number;
  shouldJumpOptiFinish: boolean;
  jumpMinutes: number;
}

/**
 * Case A (remaining work >= break): jump the finish past the break.
 * Case B (remaining work < break): hold the finish; the difference is a buffer the overrun consumes first.
 * (moved from breakBufferHelpers)
 */
export function computeBreakBufferAction(
  remainingMinutes: number,
  breakDurationMinutes: number,
  currentBufferRemaining: number | null,
): BreakBufferResult {
  if (currentBufferRemaining && currentBufferRemaining > 0) {
    return { case: 'B', breakBufferRemaining: currentBufferRemaining, shouldJumpOptiFinish: false, jumpMinutes: 0 };
  }
  if (remainingMinutes >= breakDurationMinutes) {
    return { case: 'A', breakBufferRemaining: 0, shouldJumpOptiFinish: true, jumpMinutes: breakDurationMinutes };
  }
  return {
    case: 'B',
    breakBufferRemaining: breakDurationMinutes - remainingMinutes,
    shouldJumpOptiFinish: false,
    jumpMinutes: 0,
  };
}

/** Consume the break buffer before pushing the finish. (moved from breakBufferHelpers) */
export function consumeBreakBuffer(
  currentBuffer: number,
  minutesPast: number,
): { consumedMinutes: number; remainingBuffer: number; shouldPushOptiFinish: boolean; pushMinutes: number } {
  if (currentBuffer <= 0) {
    return { consumedMinutes: 0, remainingBuffer: 0, shouldPushOptiFinish: true, pushMinutes: minutesPast };
  }
  if (minutesPast <= currentBuffer) {
    return { consumedMinutes: minutesPast, remainingBuffer: currentBuffer - minutesPast, shouldPushOptiFinish: false, pushMinutes: 0 };
  }
  return { consumedMinutes: currentBuffer, remainingBuffer: 0, shouldPushOptiFinish: true, pushMinutes: minutesPast - currentBuffer };
}

// ─── the finish of a window ─────────────────────────────────────────────────

/**
 * THE finish of a work window: the start instant plus the window's elapsed minutes
 * (work + the breaks it spans, computed on the wall clock). DST-exact: the old
 * `makeUTCFromShopMinutes(day, finishMinute)` re-read the finish's wall minute on the
 * start's date and was off by the offset change whenever the window crossed it.
 */
export function finishFromStart(startMs: number, startMinOfDay: number, wallMinutes: number, breaks: BreakMinutes[]): number {
  const finishMin = calculateTicketOptiFinish(startMinOfDay, wallMinutes, breaks);
  return windowFinishInstant(startMs, startMinOfDay, finishMin);
}

/** The instant a window ends, given its start instant and its start/finish shop-local minutes of day. */
export function windowFinishInstant(startMs: number, startMinOfDay: number, finishMinOfDay: number): number {
  return startMs + (finishMinOfDay - startMinOfDay) * MINUTE_MS;
}

export function ceilToMinute(ms: number): number {
  return Math.ceil(ms / MINUTE_MS) * MINUTE_MS;
}

/**
 * AutoBump's new start for a not-started anchor: the next whole minute, moved past any
 * break it falls strictly inside (one pass, in the technician's break order).
 */
export function bumpStart(nowMs: number, breakWindowsMs: Array<{ startMs: number; endMs: number }>): number {
  let start = ceilToMinute(nowMs);
  for (const brk of breakWindowsMs) {
    if (start > brk.startMs && start < brk.endMs) start = ceilToMinute(brk.endMs);
  }
  return start;
}

// ─── measured work ──────────────────────────────────────────────────────────

export type TimerSession = { duration_min: number | null; started_at: string | null; stopped_at: string | null };

/** Worked minutes: closed sessions' measured minutes + the live session's whole minutes so far. */
export function workedMinutes(sessions: TimerSession[], nowMs: number): number {
  let worked = 0;
  for (const s of sessions) {
    if (s.stopped_at) worked += s.duration_min || 0;
    else if (s.started_at) worked += Math.trunc((nowMs - Date.parse(s.started_at)) / MINUTE_MS);
  }
  return worked;
}

/** Worked minutes of CLOSED sessions only (the break-buffer remaining-work rule). */
export function closedWorkedMinutes(sessions: TimerSession[]): number {
  return sessions.reduce((sum, s) => sum + (s.stopped_at ? s.duration_min || 0 : 0), 0);
}

// ─── the intents ────────────────────────────────────────────────────────────

/** Running past OptiTime: how long it has run long (whole minutes, never negative). */
export function runLongMinutes(worked: number, optiTime: number): number {
  return Math.max(0, Math.ceil(worked - optiTime));
}

/**
 * oct20d CORE-BF :41 (founder Decision 1, 2026-10-22) — THE expectation run-long is measured against, for the server
 * (finishIntentServer, the run-long-autobump planner) and the Dayboard card (derivedTicketTime) alike.
 *
 * The frozen baseline `tickets.run_long_opti_baseline` is server-owned (20270210000000): frozen when work begins and
 * revised only by a recorded change to the ticket's job lines (ticket_run_long_baseline_revisions). Pushback,
 * cascades, rollovers and projected-finish moves never touch it. Only work whose expectation could not be frozen
 * (no known duration or no technician when it began) falls back to the CURRENT OptiTime.
 */
export function runLongBaselineMinutes(frozenBaseline: number | null | undefined, currentOptiTime: number | null): number | null {
  return typeof frozenBaseline === 'number' && Number.isFinite(frozenBaseline) && frozenBaseline > 0 ? frozenBaseline : currentOptiTime;
}

/** A running ticket that has run long is expected to finish five minutes from now (AutoBump's rule). */
export const RUN_LONG_REMAINING_MINUTES = 5;
export function runLongFinish(nowMs: number): number {
  return nowMs + RUN_LONG_REMAINING_MINUTES * MINUTE_MS;
}

/** Wall minutes since the work first started that were not worked. */
export function pausedMinutes(nowMs: number, actualStartMs: number, worked: number): number {
  return Math.max(0, Math.trunc((nowMs - actualStartMs) / MINUTE_MS) - worked);
}

/**
 * THE paused finish: now plus the work that remains. AutoBump derived it this way;
 * the pushback hook derived the same state as `actual start + booked minutes + pause`
 * — book minutes, not OptiTime — so the two rewrote the same row with two different
 * finishes every minute. This is the one derivation both now request.
 */
export function pausedFinish(nowMs: number, optiTime: number, worked: number): number {
  return nowMs + Math.max(0, optiTime - worked) * MINUTE_MS;
}

/** Locked at the end of the day (never before the ticket's own start). */
export function eodLockFinish(shiftEndMs: number, startMs: number | null): number {
  return startMs != null && Number.isFinite(startMs) && startMs > shiftEndMs ? startMs : shiftEndMs;
}

export interface PushbackInput {
  nowMs: number;
  finishMs: number;
  startMinOfDay: number;
  finishMinOfDay: number;
  breaks: DurationBreak[];
  /** OptiTime minus closed-session work, never negative (calculateRemainingMinutes). */
  remainingMinutes: number;
  breakBufferRemaining: number | null;
}

export interface PushbackPlan {
  minutesPast: number;
  pushMinutes: number;
  newFinishMs: number;
  breakBufferRemaining: number | null;
}

/**
 * Run past the finish: push it by the overrun, jumping a newly-entered break (Case A)
 * or letting the break buffer absorb the overrun first (Case B). Null when the finish
 * has not passed.
 */
export function pushbackPlan(input: PushbackInput): PushbackPlan | null {
  if (!(input.nowMs > input.finishMs)) return null;
  const minutesPast = Math.ceil((input.nowMs - input.finishMs) / MINUTE_MS);
  let buffer: number | null = input.breakBufferRemaining ?? null;
  let push = minutesPast;
  const spanned = ticketSpansBreak(input.startMinOfDay, input.finishMinOfDay + minutesPast, input.breaks);
  if (spanned) {
    const wasSpanning = ticketSpansBreak(input.startMinOfDay, input.finishMinOfDay, input.breaks);
    if (!wasSpanning) {
      const action = computeBreakBufferAction(input.remainingMinutes, spanned.durationMinutes, input.breakBufferRemaining);
      if (action.case === 'A') {
        push = minutesPast + action.jumpMinutes;
        buffer = null;
      } else if (action.case === 'B') {
        const consumed = consumeBreakBuffer(action.breakBufferRemaining, minutesPast);
        push = consumed.pushMinutes;
        buffer = consumed.remainingBuffer > 0 ? consumed.remainingBuffer : null;
      }
    } else if (buffer && buffer > 0) {
      const consumed = consumeBreakBuffer(buffer, minutesPast);
      push = consumed.pushMinutes;
      buffer = consumed.remainingBuffer > 0 ? consumed.remainingBuffer : null;
    }
  } else {
    buffer = null;
  }
  return { minutesPast, pushMinutes: push, newFinishMs: input.finishMs + push * MINUTE_MS, breakBufferRemaining: buffer };
}
